/* eslint-disable react/jsx-key */
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import StickyBox from "react-sticky-box";
import { SurveyLogin, SurveyMap } from "senf-atomic-design-system";
import { tv } from "tailwind-variants";

import type {
  Question,
  QuestionColumn,
  Result,
} from "senf-atomic-design-system/src/components/special/survey/survey.types";

import { usePrepareSurveyPage } from "senf-atomic-design-system/src/components/special/survey/hooks";

import type { FormikProps } from "formik";
import type { Map as MapType } from "mapbox-gl";
import type { Project, User } from "senf-shared/types";
import SurveyImages from "./components/SurveyImages";

import { getInitialsColor } from "senf-shared";
import { useIsHorizontal } from "../../../hooks/customDeviceDetect";
import { useMeasure } from "../../../hooks/useMeasure";
import SurveyQuestion, { useDefaultValues } from "./SurveyQuestion";

const SurveyBuilderStyles = tv({
  slots: {
    base: "group m-auto h-full min-h-0 w-full overflow-y-auto overflow-x-visible bg-white",
    Page: "relative inset-0 grid",
    ImageContainer: "relative flex w-full justify-center",
    MapContainer: "relative h-full items-center justify-center",
    QuestionContainer:
      "flex w-full items-center justify-center self-start group-[.has-image]:items-start",
    QuestionWrapper: "w-full max-w-[720px]",
  },
  variants: {
    horizontal: {
      true: {},
      false: {},
    },
    isMobile: {
      false: {
        base: "max-h-[720px] min-h-[360px] min-w-[640px] max-w-[1280px]",
        Page: "grid-cols-[repeat(auto-fit,_minmax(100px,_1fr))]",
        ImageContainer:
          "p-10 pr-5 group-[:not(.cover)]:self-start group-[.cover]:p-0",
        MapContainer: "max-h-full p-10 group-[.cover]:p-0",
        QuestionWrapper: "my-auto p-10 group-[.has-image:not(.cover)]:pl-5",
      },
      true: {
        base: "max-h-[720px] min-w-[375px] auto-rows-[auto_1fr]",
        Page: "h-full",
        ImageContainer: "p-0 group-[.cover]:p-0",
        MapContainer:
          "h-[calc(100vh_-_76px)] p-0 group-[.map-based]:order-1 group-[.fullscreen-map]:-mt-20 group-[.fullscreen-map]:h-[calc(100vh_-_76px)] group-[.cover]:p-0",
        QuestionContainer:
          "max-w-screen z-10 mx-auto h-full rounded-t-2xl bg-white duration-100 group-[.fullscreen-map]:h-0 group-[.has-image]:max-h-full group-[.fullscreen-map]:overflow-hidden",
        QuestionWrapper: "mb-56 p-4 group-[.map-based]:mb-4",
      },
    },
    mode: {
      edit: {
        base: "rounded-md shadow overflow-x-auto",
      },
      display: {
        base: "h-full max-h-full w-full max-w-full overscroll-x-none overflow-x-hidden",
      },
    },
    format: {
      cover: "cover",
    },
    mapBased: {
      true: "map-based",
    },
    hasImage: {
      true: "has-image",
    },
    hasMap: {
      true: "has-map",
    },
    fullscreenMap: {
      true: "fullscreen-map",
    },
  },
  compoundVariants: [
    {
      isMobile: true,
      mode: "edit",
      class: {
        base: "max-h-[720px] min-h-[540px] max-w-[375px]",
        QuestionWrapper: "max-w-[375px]",
      },
    },
    {
      isMobile: true,
      mode: "display",
      class: {
        base: "max-w-[100vw]",
        QuestionWrapper: "max-w-[100vw]",
      },
    },
    {
      isMobile: true,
      horizontal: true,
      class: "",
    },
    {
      isMobile: true,
      horizontal: false,
      class: "min-h-[540px]",
    },
  ],
  defaultVariants: {
    isMobile: false,
  },
});

export interface SurveyWrapperProps {
  surveyId?: string;
  project: Project;
  pages: Question[];
  setPages?: Dispatch<SetStateAction<Question[]>>;
  loadingImage?: boolean;
  setLoadingImage?: Dispatch<SetStateAction<boolean>>;
  activePage: string | null;
  mode: "edit" | "display";
  isMobile: boolean;
  formik: FormikProps<Result>;
  loginFormik?: FormikProps<User>;
  demographicsFormik?: FormikProps<User>;
  surveyLoginFormShown?: boolean;
  setSurveyLoginFormShown?: Dispatch<SetStateAction<boolean>>;
  openDescription?: boolean;
  setOpenDescription?: Dispatch<SetStateAction<boolean>>;
  featureId?: string;
  setFeatureId?: Dispatch<SetStateAction<string | undefined>>;
  fullscreenMap?: boolean;
  setFullscreenMap?: Dispatch<SetStateAction<boolean>>;
  outroButtons?: any;
  imageFocus?: number | false;
  sidebars?: {
    collapsed: {
      left: boolean;
      right: boolean;
    };
  };
}

const SurveyWrapper = (props: SurveyWrapperProps) => {
  const {
    project,
    pages,
    activePage,
    mode,
    isMobile,
    loadingImage,
    setLoadingImage,
    loginFormik,
    surveyLoginFormShown,
    setSurveyLoginFormShown,
    fullscreenMap,
    setPages,
    imageFocus,
    sidebars,
    formik,
  } = props;
  const [statefulMap, setStatefulMap] = useState<MapType>();
  const [{ ref: pageRef }, { height }] = useMeasure<HTMLDivElement>();

  const currentIndex = Math.max(
    0,
    pages?.findIndex((e) => e.uid === activePage),
  );

  const {
    currentPage,
    currentPageHasImage,
    currentPageHasMap: hasMap,
    currentPageIsMapbased,
  } = usePrepareSurveyPage({ pages, index: currentIndex, isMobile, project });

  const currentPageHasMap = hasMap && !surveyLoginFormShown;

  const currentPageHasLoadingImage =
    (currentPageHasImage || loadingImage) && !surveyLoginFormShown;
  const { defaultOtherTitle } = useDefaultValues(currentPage);
  const {
    base,
    Page,
    ImageContainer,
    MapContainer,
    QuestionContainer,
    QuestionWrapper,
  } = SurveyBuilderStyles({
    mode,
    isMobile,
    horizontal: useIsHorizontal(),
    mapBased: currentPageIsMapbased,
    hasImage: currentPageHasLoadingImage && !currentPageHasMap,
    hasMap: currentPageHasMap,
    fullscreenMap,
    format:
      currentPageHasMap ||
      (currentPage?.imgFormat === "full" &&
        (typeof currentPage?.imgUrl === "string" ||
          currentPage?.imgUrl?.length === 1))
        ? "cover"
        : undefined,
  });

  const StickyDiv = isMobile ? "div" : StickyBox;

  const handleSaveDrawn = (polygon) => {
    const uid = currentPage?.uid;
    if (polygon && polygon.features && polygon.features.length > 0) {
      const validFeatures = polygon.features.filter(
        (feature) =>
          feature.geometry.coordinates &&
          feature.geometry.coordinates.length > 0,
      );

      const updatedFeatures = validFeatures.map((feature, index) => {
        const finalFeature =
          feature ||
          (formik.values[uid]?.geoData &&
            JSON.parse(formik.values[uid].geoData)?.features?.[index]);

        const options = [
          ...currentPage?.options,
          currentPage.mapbasedOtherOption
            ? ({
                value: currentPage.otherTitle || defaultOtherTitle,
                uid: "other",
              } as QuestionColumn)
            : undefined,
        ].filter(Boolean);

        return currentPage?.mapbasedQtype === "textarea"
          ? {
              ...finalFeature,
              properties: {
                ...finalFeature.properties,
                description: finalFeature?.properties?.description,
                number: index + 1,
              },
            }
          : currentPage?.mapbasedQtype === "radio"
            ? {
                ...finalFeature,
                properties: {
                  ...finalFeature.properties,
                  number: index + 1,
                  choices: finalFeature?.properties?.choices,
                  color: getInitialsColor(
                    finalFeature.properties.choices === "other"
                      ? "other"
                      : options?.findIndex(
                          (item) =>
                            item.uid === finalFeature?.properties?.choices,
                        ),
                    "css",
                  ),
                },
              }
            : {
                ...finalFeature,
                properties: {
                  ...finalFeature.properties,
                  number: index + 1,
                },
              };
      });

      const updatedGeoData = {
        type: "FeatureCollection",
        features: updatedFeatures,
      };

      formik?.setFieldValue(uid, {
        ...formik.values[uid],
        geoData: JSON.stringify(updatedGeoData),
        geoDataType: pages?.[currentIndex]?.qtype,
      });
      return updatedGeoData;
    }
    formik?.setFieldValue(uid, {
      ...formik.values[uid],
      geoData: null,
    });
    return {};
  };

  // this is necessary so that focussing on overflown inputs does not scroll the surveywrapper
  useEffect(() => {
    const handlePreventScroll = () => {
      if (
        mode === "display" &&
        pageRef.current &&
        pageRef.current?.scrollLeft !== 0
      ) {
        pageRef.current.scrollLeft = 0;
      }
    };
    pageRef.current?.addEventListener("scroll", handlePreventScroll);
    return () => {
      pageRef.current?.removeEventListener("scroll", handlePreventScroll);
    };
  }, [mode]);

  return (
    <div className={base()} id="surveyWrapper" ref={pageRef}>
      <div className={Page()}>
        {currentPageHasLoadingImage && !currentPageHasMap && (
          <StickyBox
            className={ImageContainer()}
            style={{
              maxHeight: isMobile ? height / 2 : undefined,
              minHeight: isMobile ? undefined : height,
              height: currentPage.imgUrl?.length === 1 ? height : undefined,
            }}
          >
            <SurveyImages
              {...props}
              page={currentPage}
              setPages={setPages}
              pageIndex={currentIndex}
              loadingImage={loadingImage}
              setLoadingImage={setLoadingImage}
              isMobile={isMobile}
              outerHeight={height}
              imageFocus={imageFocus}
            />
          </StickyBox>
        )}
        {currentPageHasMap && (
          <StickyBox
            className={MapContainer()}
            style={{
              minHeight: isMobile ? undefined : undefined,
              maxHeight:
                isMobile && !currentPageIsMapbased ? "50vh" : undefined,
              height:
                mode === "edit"
                  ? height
                  : isMobile
                    ? height - 40
                    : `${window.innerHeight}px`,
            }}
          >
            <SurveyMap
              {...props}
              index={currentIndex}
              uid={currentPage?.uid}
              statefulMap={statefulMap}
              setStatefulMap={setStatefulMap}
              setPages={setPages}
              resize={Object.values(sidebars?.collapsed || {}).toString()}
              handleSaveDrawn={handleSaveDrawn}
            />
          </StickyBox>
        )}
        <StickyDiv
          className={QuestionContainer()}
          style={{
            minHeight: isMobile ? undefined : height,
          }}
        >
          <div className={QuestionWrapper()}>
            {surveyLoginFormShown ? (
              <SurveyLogin
                formik={loginFormik}
                setSurveyLoginFormShown={setSurveyLoginFormShown}
                project={project}
              />
            ) : (
              <SurveyQuestion
                {...props}
                pageIndex={currentIndex}
                loadingImage={loadingImage}
                statefulMap={statefulMap}
                handleSaveDrawn={handleSaveDrawn}
              />
            )}
          </div>
        </StickyDiv>
      </div>
    </div>
  );
};

export default SurveyWrapper;
